import { Survey, SurveyResponse, AlarmData } from '../types/survey';

// Calculate risk score based on responses using the new scoring system
export const calculateRiskScore = (survey: Survey, responses: SurveyResponse[]): number => {
	let totalScore = 0;

	responses.forEach((response) => {
		// Handle both new structure and legacy compatibility
		const question = survey.questions?.find((q) => q.id === response.questionId);
		if (!question) return;

		// Use the score from the question (new API structure) or fallback to riskWeight (legacy)
		const questionScore = question.score || (question as any).riskWeight || 0;

		// For boolean questions, only add score if answer is true (indicates risk)
		// Ignore null answers (unanswered questions)
		if (response.answer === true) {
			totalScore += questionScore;
		}
	});

	return Math.round(totalScore * 10) / 10; // Round to 1 decimal place
};

// Determine if alarm should be triggered based on scoring from backend
export const shouldTriggerAlarm = (riskScore: number, threshold: number = 15): boolean => {
	return riskScore >= threshold;
};

// Get alarm severity based on risk score
export const getAlarmSeverity = (riskScore: number, threshold: number = 15): AlarmData['severity'] => {
	if (riskScore < threshold) return 'low';
	if (riskScore < threshold * 1.5) return 'medium';
	if (riskScore < threshold * 2) return 'high';
	return 'critical';
};

// Create alarm data object
export const createAlarmData = (surveyId: string, riskScore: number, responses: SurveyResponse[], threshold: number = 15): AlarmData => ({
	surveyId,
	riskScore,
	responses,
	timestamp: new Date().toISOString(),
	severity: getAlarmSeverity(riskScore, threshold),
});

// Validate that all required questions are answered
export const validateResponses = (survey: Survey, responses: SurveyResponse[]): string[] => {
	const errors: string[] = [];

	// Handle both new sections structure and legacy questions structure
	const questions = survey.questions || survey.sections?.flatMap((section) => section.questions) || [];

	questions.forEach((question) => {
		const response = responses.find((r) => r.questionId === question.id);
		if (!response || response.answer === '' || response.answer === null || response.answer === undefined) {
			const questionText = (question as any).title || (question as any).text || `Pitanje ${question.id}`;
			errors.push(`Pitanje "${questionText}" je obavezno`);
		}
	});

	return errors;
};

// Group questions by section (updated for new API structure)
export const getQuestionsBySection = (survey: Survey) => {
	if (survey.sections) {
		// New structure with sections
		return survey.sections.reduce((acc, section) => {
			acc[section.id] = section.questions;
			return acc;
		}, {} as { [sectionId: string]: (typeof survey.sections)[0]['questions'] });
	} else if (survey.categories && survey.questions) {
		// Legacy structure with categories
		const questionsByCategory: { [categoryId: string]: typeof survey.questions } = {};
		survey.categories.forEach((category) => {
			questionsByCategory[category.id] = survey.questions!.filter((question) => (question as any).category === category.id);
		});
		return questionsByCategory;
	}

	return {};
};

// Analyze risk indicators based on the Serbian trafficking assessment criteria (updated for new structure)
export const analyzeRiskIndicators = (survey: Survey, responses: SurveyResponse[]) => {
	let strongIndicators = 0; // indicators with score 3 or higher
	let totalIndicatorsPerSection: { [sectionId: string]: number } = {};
	// Initialize section counters
	const sections = survey.sections || survey.categories || [];
	sections.forEach((section) => {
		totalIndicatorsPerSection[section.id] = 0;
	});

	responses.forEach((response) => {
		// Handle both new structure and legacy compatibility
		const questions = survey.questions || survey.sections?.flatMap((section) => section.questions) || [];
		const question = questions.find((q) => q.id === response.questionId);
		if (!question) return;

		const questionScore = question.score || (question as any).riskWeight || 0;
		const sectionId = (question as any).sectionId || (question as any).category;

		let isIndicatorPresent = false;

		// For boolean questions (most common in trafficking assessment)
		// Only count as indicator if explicitly answered with true
		if (response.answer === true) {
			isIndicatorPresent = true;
		}

		if (isIndicatorPresent && questionScore > 0) {
			// Check if this is a strong indicator (score 3 or higher)
			if (questionScore >= 3) {
				strongIndicators++;
			}
			// Add to section total if we can determine the section
			if (sectionId && totalIndicatorsPerSection.hasOwnProperty(sectionId)) {
				totalIndicatorsPerSection[sectionId]++;
			}
		}
	});

	// Check if any section has 4 or more indicators
	const hasHighSectionScore = Object.values(totalIndicatorsPerSection).some((count) => count >= 4);

	return {
		strongIndicators,
		totalIndicatorsPerSection,
		hasHighSectionScore,
		hasStrongIndicator: strongIndicators > 0,
		hasWeakIndicators: Object.values(totalIndicatorsPerSection).some((count) => count > 0 && count < 4) && strongIndicators === 0 && !hasHighSectionScore,
		hasNoIndicators: Object.values(totalIndicatorsPerSection).every((count) => count === 0),
	};
};

// Get assessment result message based on indicators
export const getAssessmentResultMessage = (survey: Survey, responses: SurveyResponse[]) => {
	const analysis = analyzeRiskIndicators(survey, responses);

	// Strong indicators or high section score - High Risk
	if (analysis.hasStrongIndicator || analysis.hasHighSectionScore) {
		return {
			type: 'high-risk' as const,
			title: 'Pronađeni su snažni indikatori',
			message: `Na osnovu vaših odgovora, pronađeni su snažni indikatori, koji ukazuju na to da se radi o mogućoj žrtvi trgovine ljudima.

Potrebno je da u skladu sa svojim nadležnostima i kompetencijama pružite pomoć osobi za koju sumnjte da je žrtva trgovine ljudima. Da joj u skladu sa procenom omogućite pružanje medicinske pomoći ili da je odvedete na sigurno mesto.

Sumnju na trgovinu ljudima potrebno je prijaviti nadležnoj policijskoj upravi na broj 192 i Centru za zaštitu žrtava trgovine ljudima.

Napominjemo, da su indikatori samo smernice i njihovo prisustvo ne znači sigurno da se radi o žrtvi trgovine ljudima, kao što i njihovo odsustvo ne znači sigurno da se ne radi o žrtvi trgovine ljudima.

Ukoliko imate dileme oko postupanja u ovom slučaju, možete se konsultovati sa stručnim radnicima Centra za zaštitu žrtava trgovine ljudima putem telefona <a href="tel:+38163610590">+38163610590</a> ili mejlom <a href="mailto:centar@centarzztlj.rs">centar@centarzztlj.rs</a>.`,
			contactInfo: {
				phone: '+38163610590',
				email: 'centar@centarzztlj.rs',
				emergency: '192',
			},
			showReportButton: true,
			riskLevel: 'high' as const,
		};
	}

	// Weak indicators - Medium Risk
	if (analysis.hasWeakIndicators) {
		return {
			type: 'medium-risk' as const,
			title: 'Pronađeni su slabi indikatori',
			message: `Na osnovu vaših odgovora, pronađeni su slabi indikatori.

Napominjemo, da su indikatori samo smernice i njihovo prisustvo ne znači sigurno da se radi o žrtvi trgovine ljudima, kao što i njihovo odsustvo ne znači sigurno da se ne radi o žrtvi trgovine ljudima.

Često se dešava da se u ovakvim situacijama ne radi o trgovini ljudima, ali postoje neki drugi oblici zlostavljanja i druga krivična dela ili postoji rizik od njih. U tom slučaju je da u skladu sa svojim nadležnostima pružite podršku osobi u riziku i da obavestite neki drugi organ odnosno ustanovu, kao što je policija, centar za socijalni rad ili neku od organizacija civilnog društva.

Potrebno je da pratite situaciju, održite kontakt sa osobom za koju ste posumnjali da je žrtva trgovine ljudima i ostavite joj mogućnost da vam se obrati za pomoć ukoliko joj bude potrebna.

Ukoliko imate dilemu o tome da li se radi o žrtvi trgovine ljudima, možete se konsultovati sa stručnim radnicima Centra za zaštitu žrtava trgovine ljudima putem telefona <a href="tel:+38163610590">+38163610590</a> ili mejlom <a href="mailto:centar@centarzztlj.rs">centar@centarzztlj.rs</a>.`,
			contactInfo: {
				phone: '+38163610590',
				email: 'centar@centarzztlj.rs',
			},
			showReportButton: true,
			riskLevel: 'medium' as const,
		};
	}

	// No indicators - Low Risk
	return {
		type: 'low-risk' as const,
		title: 'Nisu pronađeni indikatori',
		message: `Na osnovu vaših odgovora, nisu pronađeni indikatori koji ukazuju na to da se radi o žrtvi trgovine ljudima.

Napominjemo, da su indikatori samo smernice i njihovo prisustvo ne znači sigurno da se radi o žrtvi trgovine ljudima, kao što i njihovo odsustvo ne znači sigurno da se ne radi o žrtvi trgovine ljudima.

Ukoliko imate sumnju da se radi o žrtvi trgovine ljudima, možete se konsultovati sa stručnim radnicima Centra za zaštitu žrtava trgovine ljudima putem telefona <a href="tel:+38163610590">+38163610590</a> ili mejlom <a href="mailto:centar@centarzztlj.rs">centar@centarzztlj.rs</a>.

Često se dešava da se u ovakvim situacijama ne radi o trgovini ljudima, ali postoje neki drugi oblici zlostavljanja i druga krivična dela ili postoji rizik od njih. U tom slučaju je potrebno da obavestite neki drugi organ odnosno ustanovu, kao što je policija, centar za socijalni rad ili neku od organizacija civilnog društva.

Preporučujemo da održite kontakt sa osobom za koju ste posumnjali da je žrtva trgovine ljudima i ostavite joj mogućnost da vam se obrati za pomoć ukoliko joj bude potrebna.`,
		contactInfo: {
			phone: '+38163610590',
			email: 'centar@centarzztlj.rs',
		},
		showReportButton: false,
		riskLevel: 'low' as const,
	};
};

export const getRiskLevelClass = (riskLevel: 'low' | 'medium' | 'high') => {
	if (riskLevel === 'high') return 'risk-high';
	if (riskLevel === 'medium') return 'risk-medium';
	return 'risk-low';
};

export const getRiskLevelText = (riskLevel: 'low' | 'medium' | 'high') => {
	if (riskLevel === 'high') return 'Visok rizik';
	if (riskLevel === 'medium') return 'Srednji rizik';
	return 'Nizak rizik';
};

export const formatAnswer = (answer: any): string => {
	if (typeof answer === 'boolean') {
		return answer ? 'Da' : 'Ne';
	}
	if (typeof answer === 'number') {
		return answer.toString();
	}
	return String(answer || 'Nema odgovora');
};

export const getTargetGroupText = (targetGroup: 'kids' | 'health-workers' | 'adults') => {
	const targetGroupMap = {
		kids: 'Deca',
		'health-workers': 'Zdravstveni radnici',
		adults: 'Odrasli',
	};
	return targetGroupMap[targetGroup] || 'Nepoznato';
};
